import React, { useContext, useEffect, useRef, useState } from 'react';
import Web3 from 'web3';
import { useAccount, usePublicClient, useWalletClient, useWaitForTransactionReceipt } from 'wagmi';

import CardBaseFC, { RoundNotLoadedYet } from 'components/CardBase';
import { NetworkCurrencySymbol, FiatCurrencySymbol } from 'components/CurrencySymbols';
import {
  LOCAL_STORAGE_PREFIX, GAS_INCREMENTS, BET_SIZE_PRECISION_DECIMALS, FIAT_CURRENCIES, MIN_BETS,
  ABI,
} from 'const';
import { ToastErrorTx, ToastProgressTx, ToastSuccessTx } from 'utils/Toasts/transaction';
import { useFiatRates, useGameDetails, useGasPrice, useNextRound, useStateWithLocalStorage_bool, useUserAlreadyReferred, useUserBalance } from 'utils/Hooks';
import { UsingDevKey } from 'utils/Dev';
import { CurrencyContext, MobileContext } from 'context';
import { GetReferrerFromLocalStorage } from 'utils/Referrals';
import { GetPredictionContractAddr } from 'context/url_based_context';
import ThumbsUp from 'assets/art/paws_up_transparent_tiny.webp';
import ThumbsDown from 'assets/art/paws_down_transparent_tiny.webp';
import { ReactComponent as GasPump } from 'assets/icons/GasPump.svg';
import { ReactComponent as BackArrow } from 'assets/icons/LeftArrowFat.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronCircleDown, faChevronCircleUp } from '@fortawesome/free-solid-svg-icons';
import { ReactComponent as DisketteUncolored } from 'assets/icons/DisketteUncolored.svg';
import { ReactComponent as DisketteColored } from 'assets/icons/DisketteColored.svg';
import './newSlider.scss';
import '../styles.scss';



function CardNextBetting(props) {
  const minBalanceAllowedAfterBet = Object.freeze({
    wei: Web3.utils.toWei('0.0015', 'Ether'),
    ether: 0.0015,
  });
  const startedHoldingDownIncrement = useRef(null);
  const holdingDownIncrement = useRef(null);
  const startedHoldingDownDecrement = useRef(null);
  const holdingDownDecrement = useRef(null);

  const isMobile = useContext(MobileContext);
  const { currencyID } = useContext(CurrencyContext);

  const gameDetails = useGameDetails();
  const minBetEther = MIN_BETS[gameDetails.wantedChain.name];
  const userBalance = useUserBalance();
  const round = useNextRound();
  const fiatRates = useFiatRates();
  const userAlreadyReferred = useUserAlreadyReferred();
  const baseGasPrice = useGasPrice();

  const walletClient = useWalletClient({ chainId: gameDetails.wantedChain.id });
  const publicClient = usePublicClient({ chainId: gameDetails.wantedChain.id });
  const account = useAccount();

  const [betButtonEnabled, SetWhetherBetButtonEnabled] = useState(true);
  const [gasControlsHidden, SetWhetherGasControlsHidden] = useState(true);
  const [betSizeEther, SetBetSizeEther] = useState(0);
  const [betSizeFiat, SetBetSizeFiat] = useState(0);
  const [fractionOfMaxBet, SetFractionOfMaxBet] = useState(0);
  const [gasSetting, SetGasSetting] = useState('Normal');
  const [gasPrice, SetGasPrice] = useState('0');
  const [saveLastBet, SetWhetherToSaveLastBet] = useStateWithLocalStorage_bool('saveLastBet', true);

  const [betTxHash, SetBetTxHash] = useState(null);
  const [formattedError, SetFormattedError] = useState('');

  //const { data: lastTxHash, error: contractError, isPending: isWritePending, writeContract } = useWriteContract();
  /*
    Why are we using this together with the waitForTransactionReceipt action?
    BECAUSE: the hook correctly detects errors. But always reports "isSuccess" == false
    And the action reports success correctly, but has no clue about the errors
  */
  const {
    isSuccess: isTxConfirmed_hook, failureReason: txFailReason
  } = useWaitForTransactionReceipt({ hash: betTxHash, confirmations: 1 });


  useEffect(() => {
    LoadStoredGasPrice();
  }, [gasPrice, gasSetting]);

  useEffect(() => {
    LoadLastBet();
  }, [userBalance]);

  useEffect(() => {
    SetBetSizeFiat((betSizeEther * GetFiatExchangeRate()).toFixed(2));
  }, [currencyID, betSizeEther]);

  useEffect(() => {
    DetermineAndSetGasPriceFromSetting(gasSetting);
  }, [gasSetting, baseGasPrice]);


  useEffect(() => {
    DetermineTxStatusFromHooks()
  }, [isTxConfirmed_hook, betTxHash, txFailReason]);

  useEffect(() => {
    HandleTxError();
  }, [formattedError, betTxHash]);


  function DetermineTxStatusFromHooks() {
    if (isTxConfirmed_hook && betTxHash) {
      console.info(`TX CONFIRMED by hook`);
      ShowTxSuccessToast(betTxHash);
    } else if (txFailReason) {
      /*
      for (const [key, value] of Object.entries(txFailReason)) {
        console.log({ [key]: value });
      }
      */
      console.info(`TX REVERTED according to hook`);
      let reason = 'unknown error';
      const matches = txFailReason.details.match(/:(.*): 0x/);
      if (matches && matches[1])
        reason = matches[1].trim();
      else
        reason = txFailReason.shortMessage.slice(0, 40);
      SetFormattedError(`${reason}`);
    }
  }

  function ShowTxPendingToast() {
    ToastProgressTx();
  }

  function HandleTxError() {
    if (formattedError) {
      ToastErrorTx(formattedError, betTxHash);
      SetBetTxHash(null); // stop useWaitForTransactionReceipt hook from further updates
      SetWhetherBetButtonEnabled(true);
      SetFormattedError('');
    }
  }

  function ShowTxSuccessToast(_txHash) {
    if (!_txHash)
      throw new SyntaxError(`missing transaction hash when trying to show success toast`);
    ToastSuccessTx(_txHash);
    SetBetTxHash(null); // stop useWaitForTransactionReceipt hook from further updates
    SetWhetherBetButtonEnabled(true);
  }


  function GetMaxBetEther() {
    const maxBetEther = userBalance.ether - minBalanceAllowedAfterBet.ether;
    if (maxBetEther < 0)
      return 0;
    else
      return maxBetEther
  }

  function GetBetSizeWei() {
    let betSizeWei;
    const betSizeEtherTruncated = Number.parseFloat(betSizeEther).toFixed(BET_SIZE_PRECISION_DECIMALS);
    try {
      betSizeWei = Web3.utils.toWei(betSizeEtherTruncated, 'Ether');
    } catch (error) {
      console.error(error);
      betSizeWei = 0;
    }
    return betSizeWei;
  }

  function GetFiatExchangeRate() {
    const wantedTickerSymbol = FIAT_CURRENCIES[currencyID]?.symbol || 'usd';
    if (!wantedTickerSymbol || !fiatRates[wantedTickerSymbol])
      return 1;
    else
      return fiatRates[wantedTickerSymbol];
  }

  function GetBetFunction() {
    if (props.bettingOnBear)
      return 'user_BetBear';
    else
      return 'user_BetBull';
  }

  function GetBetFunctionWithReferrals() {
    if (props.bettingOnBear)
      return 'user_BetBearSpecial';
    else
      return 'user_BetBullSpecial';
  }

  function EnforceMinMaxBetValue(newBetValue) {
    if (newBetValue == undefined || newBetValue == null)
      throw new SyntaxError('Missing required argument: newBetValue');

    if (newBetValue == 0)
      return 0;
    else if (Number(newBetValue) < minBetEther)
      return minBetEther.toFixed(BET_SIZE_PRECISION_DECIMALS);
    else if (Number(newBetValue) > GetMaxBetEther())
      return GetMaxBetEther().toFixed(BET_SIZE_PRECISION_DECIMALS);
    else
      return newBetValue;
  }

  function ParseNumberAndEnforceBetSize(rawInput) {
    if (rawInput == undefined) throw new SyntaxError('Missing required argument: rawInput')
    const betSize = rawInput.replace(/[^0-9.]/g, '');
    return EnforceMinMaxBetValue(betSize);
  }

  function ParseNumber(rawInput) {
    if (rawInput == undefined) throw new SyntaxError('Missing required argument: rawInput')
    return rawInput.replace(/[^0-9.]/g, '');
  }

  function FiatFromEther(value) {
    return (value * GetFiatExchangeRate()).toFixed(2);
  }
  function EtherFromFiat(value) {
    return (value / GetFiatExchangeRate()).toFixed(BET_SIZE_PRECISION_DECIMALS);
  }

  function GetBetSizeFromFraction(percent) {
    if (percent === undefined) throw new SyntaxError('missing required argument: percent');
    return (GetMaxBetEther() * (percent / 100)).toFixed(BET_SIZE_PRECISION_DECIMALS)
  }

  function LoadLastBet() {
    let lastBet = localStorage.getItem(LOCAL_STORAGE_PREFIX + 'lastBet');
    if (isNaN(lastBet))
      return;
    lastBet = EnforceMinMaxBetValue(Number(lastBet));
    SetBetSizeEther(lastBet);
    SetBetSizeFiat(FiatFromEther(lastBet));
    SetFractionOfMaxBet(Math.round((lastBet / GetMaxBetEther()) * 100));
  }

  function GetReferrerForBetting() {
    const referrerFromLocalStorage = GetReferrerFromLocalStorage();
    if (!userAlreadyReferred && referrerFromLocalStorage)
      return referrerFromLocalStorage;
    else
      return null;
  }

  function LoadStoredGasPrice() {
    const gasSetting = localStorage.getItem(LOCAL_STORAGE_PREFIX + 'gasLevel');
    if (!gasSetting)
      DetermineAndSetGasPriceFromSetting('Normal');
    else
      DetermineAndSetGasPriceFromSetting(gasSetting);
  }

  function ToggleGasControlsVisibility() {
    SetWhetherGasControlsHidden(!gasControlsHidden);
  }

  function ToggleSavingOfLastBet() {
    let newValue;
    if (saveLastBet) {
      alert('Disabled saving of last bet sizes');
      console.info('Saving of last bet disabled');
      newValue = false;
    } else {
      alert('Your last bet will now be saved and suggested when making a new bet');
      console.info('Saving of last bet enabled');
      newValue = true;
    }
    SetWhetherToSaveLastBet(newValue);
  }


  function HandleManualBetInput_crypto(event) {
    const betSize = ParseNumberAndEnforceBetSize(event.target.value);
    SetBetSizeEther(betSize);
    SetBetSizeFiat(FiatFromEther(betSize));
    SetFractionOfMaxBet(Math.round((betSize / GetMaxBetEther()) * 100));
  }

  function HandleManualBetInput_fiat(event) {
    const betSize = ParseNumber(event.target.value);
    SetBetSizeFiat(betSize);
    SetBetSizeEther(EtherFromFiat(betSize));
    SetFractionOfMaxBet(Math.round((betSize / FiatFromEther(GetMaxBetEther())) * 100));
  }

  function HandleFractionBetInput(event) {
    const percent = ParseNumber(event.target.value);
    const betSize = GetBetSizeFromFraction(percent)
    SetBetSizeEther(betSize);
    SetBetSizeFiat(FiatFromEther(betSize));
    SetFractionOfMaxBet(percent);
  }

  function IncrementFractionBetInput() {
    let percent = fractionOfMaxBet + 1;
    if (percent > 100)
      percent = 100;
    const betSize = GetBetSizeFromFraction(percent);
    SetBetSizeEther(betSize);
    SetBetSizeFiat(FiatFromEther(betSize));
    SetFractionOfMaxBet(percent);
  }

  function DecrementFractionBetInput() {
    let percent = fractionOfMaxBet - 1;
    if (percent < 0)
      percent = 0;
    const betSize = GetBetSizeFromFraction(percent);
    SetBetSizeEther(betSize);
    SetBetSizeFiat(FiatFromEther(betSize));
    SetFractionOfMaxBet(percent);
  }

  function BeginHoldDownToIncreaseBetInput() {
    startedHoldingDownIncrement.current = setTimeout(() => {
      holdingDownIncrement.current = setInterval(IncrementFractionBetInput, 75);
    }, 800);
  }
  function StopHoldingDownToIncreaseBetInput() {
    clearTimeout(startedHoldingDownIncrement.current);
    clearInterval(holdingDownIncrement.current);
  }

  function BeginHoldDownToDecreaseBetInput() {
    startedHoldingDownDecrement.current = setTimeout(() => {
      holdingDownDecrement.current = setInterval(DecrementFractionBetInput, 75);
    }, 800);
  }
  function StopHoldingDownToDecreaseBetInput() {
    clearTimeout(startedHoldingDownDecrement.current);
    clearInterval(holdingDownDecrement.current);
  }

  async function DetermineAndSetGasPriceFromSetting(setting) {
    if (setting === undefined) throw new SyntaxError('missing required argument: setting');
    //const incrementStep = GAS_INCREMENTS[gameDetails.wantedChain.name];
    const incrementStep = 0.5; // fraction of baseGas
    let incrementGwei = 0;
    switch (setting) {
      case 'Normal':
        incrementGwei = '0';
        break;
      case 'Fast':
        incrementGwei = (baseGasPrice * incrementStep).toString();
        break;
      case 'Very Fast':
        incrementGwei = (baseGasPrice * (incrementStep * 2)).toString();
        break;
      default:
        incrementGwei = '0';
        break;
    }
    if (!CurrentGasSettingAlreadySelected(setting)) {
      localStorage.setItem(LOCAL_STORAGE_PREFIX + 'gasLevel', setting);
      SetGasSetting(setting);
    }
    SetGasPrice((parseInt(baseGasPrice) + parseInt(incrementGwei)).toString());
  }

  function CurrentGasSettingAlreadySelected(setting) {
    if (!setting) throw new SyntaxError('missing required argument: setting');
    return (setting == gasSetting);
  }


  async function PlaceBet() {
    if (!account?.address || RoundNotLoadedYet(round)) {
      console.warn('no web3/contract/address or round not loaded yet');
      return;
    }
    const betSize = GetBetSizeWei();
    if (!betSize || !CheckWalletBalanceAgainst(betSize)) {
      console.warn('no bet size given/insufficient balance');
      return;
    }
    if (UsingDevKey())
      alert('You are in debug mode');

    if (saveLastBet)
      localStorage.setItem(LOCAL_STORAGE_PREFIX + 'lastBet', betSizeEther);
    let betFunctionName = '';
    const referrerAddressForBetting = GetReferrerForBetting();
    const functionArgs = [[round.epoch]];
    if (referrerAddressForBetting) {
      betFunctionName = GetBetFunctionWithReferrals();
      functionArgs.push(referrerAddressForBetting);
    } else {
      betFunctionName = GetBetFunction();
    }

    SetWhetherBetButtonEnabled(false);
    let simulationResult = null;
    try {
      simulationResult = await publicClient.simulateContract({
        //simulationResult = await customPubClient.simulateContract({
        address: GetPredictionContractAddr(gameDetails),
        abi: ABI.prediction,
        functionName: betFunctionName,
        args: functionArgs,
        account: account.address,
        value: betSize,
        //from: account.address,
        //gasPrice: gasPrice,
      });
    } catch (e) {
      if (e?.shortMessage) {
        ToastErrorTx(e.shortMessage);
      } else {
        ToastErrorTx(e.message);
      }
      console.error(e);
      SetWhetherBetButtonEnabled(true);
      return;
    }

    simulationResult.request.gasPrice = gasPrice; // ensure we've gas price set
    // cook up arguments to make sure that this fails; for debug purposes
    //simulationResult.request.args[0][0] -= 1; //debug

    let txHash = '';
    ShowTxPendingToast();
    try {
      txHash = await walletClient.data.writeContract(simulationResult.request);
      SetBetTxHash(txHash);
    } catch (e) {
      if (e?.shortMessage) {
        SetFormattedError(e.shortMessage);
      } else {
        SetFormattedError(String(e).slice(0, 40));
      }
      SetWhetherBetButtonEnabled(true);
      SetBetTxHash(null);
      return;
    }

    try {
      /*
        Why are we using this together with the useWaitForTransactionReceipt hook?
        BECAUSE: the hook correctly detects errors. But always reports "isSuccess" == false
        And the action reports success correctly, but has no clue about the errors
      */
      const txReceipt = await publicClient.waitForTransactionReceipt({
        hash: txHash,
        confirmations: 1,
        timeout: 25 * 1000,
      })
      //console.dir(txReceipt, { depth: 6 });
      if (txReceipt?.status == "success") {
        console.info(`TX CONFIRMED by direct polling`);
        ShowTxSuccessToast(txHash);
      } else if (txReceipt?.status == "reverted") {
        console.info(`TX REVERTED according to direct polling; waiting on better error message from hook`);
        // hook offers a much better error message
        //SetFormattedError(`tx reverted, unknown reason; hash: ${txHash.slice(-4)}`);
      } else {
        // assume the tx is pending or that there's an error
        console.warn(`tx is either still pending, or .status has an unpredictable value: ${txReceipt?.status}`);
      }
    } catch (e) {
      if (e?.details) {
        let reason = 'unknown error when waiting for confirmation directly';
        const matches = txFailReason.details.match(/:(.*): 0x/);
        if (matches && matches[1])
          reason = matches[1].trim();
        else
          reason = txFailReason.shortMessage.slice(0, 40);
        SetFormattedError(reason);
      } else {
        SetFormattedError(String(e).slice(0, 40));
      }
    }

    /*
        txConfirmation_p
          .then(() => SetWhetherBetButtonEnabled(true))
          .catch(() => SetWhetherBetButtonEnabled(true));
        HandleTransactionPromise(txConfirmation_p, gameDetails.wantedChain);
    */

    //  const txReceipt = await txReceiptPromise;
    //} catch (error) {
    //  console.error(error);
    //  HandleWalletError(error);
    //}
  }


  function CheckWalletBalanceAgainst(betSize) {
    if (!betSize) throw new SyntaxError('Missing required argument: betSize');
    if (betSize > userBalance.wei - minBalanceAllowedAfterBet) {
      alert('Your balance is too low to make this bet\nWoof!');
      return false;
    }
    return true;
  }


  /* === JSX methods below this line; used for drawing page layout === */

  function BetSizeNumericField_crypto() {
    return (
      <label className='ThemeBox BetSize Direct Crypto'>
        <span className='Currency'>
          <NetworkCurrencySymbol /> {gameDetails.wantedChain.tickerSymbol}
        </span>
        <input
          className='BetSize Direct Crypto'
          type='number' value={betSizeEther}
          step={minBetEther * 10} min={minBetEther} max={GetMaxBetEther()}
          onInput={HandleManualBetInput_crypto}
        />
      </label>
    );
  }

  function BetSizeNumericField_fiat() {
    return (
      <label className='ThemeBox BetSize Direct Fiat'>
        <span className='Currency'>
          <FiatCurrencySymbol
            cryptoAmount={betSizeEther}
            interactive={true}
          /> {FIAT_CURRENCIES[currencyID].symbol || 'USD'}
        </span>
        <input
          className='BetSize Direct Fiat'
          type='number' value={betSizeFiat}
          step={0.5} min={0} max={FiatFromEther(GetMaxBetEther())}
          onInput={HandleManualBetInput_fiat}
        />
      </label>
    );
  }


  function BetSizeSaverDiskette() {
    let diskette = null; let classSuffix = ''
    if (saveLastBet) {
      diskette = <DisketteColored />;
    } else {
      diskette = <DisketteUncolored />;
      classSuffix = 'Inactive'
    }
    return (
      <button className={`ThemeButton BetSavingToggle ${classSuffix}`} onClick={ToggleSavingOfLastBet}>
        {diskette}
      </button>
    );
  }


  function BetSizePercent() {
    return (
      <label className='ThemeBox BetSize Percent'>
        <input
          className='Percent'
          type='number' value={fractionOfMaxBet}
          step={1} min={0} max={100}
          onInput={HandleFractionBetInput}
        />
        <span className='ThemeWhite'>%</span>
      </label>
    );
  }

  function BetSizeSlider() {
    return (
      <div className='SliderContainer'>
        <input
          id='betSlider'
          type='range' value={betSizeEther}
          step={minBetEther} min={minBetEther} max={GetMaxBetEther()}
          onInput={HandleManualBetInput_crypto}
        />
      </div>
    );
  }

  function BetSizeMax() {
    return (<button
      className='ThemeButton FixedPercentage Max'
      onClick={() => {
        SetBetSizeEther((GetMaxBetEther()).toFixed(BET_SIZE_PRECISION_DECIMALS));
        SetBetSizeFiat(FiatFromEther(GetMaxBetEther()));
        SetFractionOfMaxBet(100);
      }}
    >
      MAX
    </button>);
  }

  function BetSizeFixedPercentageButton(percent) {
    if (!percent) throw new SyntaxError('missing required argument: percent');
    return (<button className='ThemeButton FixedPercentage'
      onClick={() => {
        SetBetSizeEther(GetBetSizeFromFraction(percent));
        SetBetSizeFiat(FiatFromEther(GetBetSizeFromFraction(percent)));
        SetFractionOfMaxBet(percent);
      }}
    >
      {percent}%
    </button>);
  }

  function GasPriceButton(level) {
    if (!level) throw new SyntaxError('missing required argument: level');
    return (<button
      className={'ThemeButton Gas' + (CurrentGasSettingAlreadySelected(level) ? ' Selected' : '')}
      onClick={() => DetermineAndSetGasPriceFromSetting(level)}
    >
      {level}
    </button>);
  }

  function PositionBeingEntered() {
    let position, color;
    if (props.bettingOnBear) {
      color = 'var(--color-bear-red)';
      position = (<img src={ThumbsDown} alt='bear' />);
    } else {
      color = 'var(--color-bull-green)';
      position = (<img src={ThumbsUp} alt='bull' />);
    }
    //Entering position: {props.bettingOnBear ? 'bear' : 'bull'}
    return (<div id='BetChosen' onClick={props.ToggleBet} style={{ backgroundColor: color }}>
      {position}
    </div>);
  }

  function WalletBalance() {
    const balance = Number.parseFloat(userBalance.ether).toFixed(4);
    return (
      <div className='ThemeBox FlexCenter Balance'>
        <p>Balance: </p>
        <p>{balance} {gameDetails.wantedChain.tickerSymbol}</p>
      </div>
    );
  }

  function GasControls() {
    let output = null;
    if (gasControlsHidden)
      output = (<button className='ThemeButton SettingsToggle'
        onClick={ToggleGasControlsVisibility}
      >
        <GasPump />
        <p>GAS ({Web3.utils.fromWei(gasPrice, 'Gwei')} Gwei)</p>
      </button>);
    else
      output = (<div className='Group'>
        {ButtonCloseGasControls()}
        {GasPriceButton("Normal")}
        {GasPriceButton("Fast")}
        {GasPriceButton("Very Fast")}
      </div>);
    return (<div className='GasSettings'>
      {output}
    </div>);
  }


  function ButtonCloseGasControls() {
    return (<button
      className={'CloseGasControls' + (isMobile ? ' Mobile' : '')}
      onClick={ToggleGasControlsVisibility}
    >
      <BackArrow />
    </button>);
  }


  function BetButton() {
    if (betButtonEnabled)
      return (
        <button className='BetButton PlaceBet' onClick={PlaceBet}>
          Place Bet
        </button>
      );
    else
      return (
        <button className='BetButton Disabled' onClick={null} >
          Place Bet
        </button>
      );
  }

  function CancelButton() {
    return (<button className='BetButton CancelBet' onClick={props.CancelBet} >
      Cancel
    </button>);
  }

  function BetIncrease() {
    return (<div className='ThemeBox BetPercentButton Increase'
      onClick={IncrementFractionBetInput}
      onPointerDown={BeginHoldDownToIncreaseBetInput}
      onPointerUp={StopHoldingDownToIncreaseBetInput}
      onPointerLeave={StopHoldingDownToIncreaseBetInput}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* <Chevron className='ChevronGraphic' alt='increase' /> */}
      <FontAwesomeIcon icon={faChevronCircleUp} />
    </div>);
  }

  function BetDescrease() {
    return (<div className='ThemeBox BetPercentButton Decrease'
      onClick={DecrementFractionBetInput}
      onPointerDown={BeginHoldDownToDecreaseBetInput}
      onPointerUp={StopHoldingDownToDecreaseBetInput}
      onPointerLeave={StopHoldingDownToDecreaseBetInput}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/*<Chevron className='ChevronGraphic' alt='decrease' />*/}
      <FontAwesomeIcon icon={faChevronCircleDown} />
    </div>);
  }


  return (
    <CardBaseFC
      cardName='SetPosition'
      round={round}
      noPremadeLayout
    >
      <div className='Header Controls'>
        {PositionBeingEntered()}
        <p>Betting on #{round ? round.epoch : '-'}</p>
      </div>
      <div className='Separator' />
      <div className={'BetControls' + (isMobile ? ' Mobile' : '')}>
        <div className='UpperSection' >
          <div className='BetSizePercentContainer'>
            {BetDescrease()}
            {BetSizePercent()}
            {BetIncrease()}
          </div>

          <div className='Group Percentages'>
            { /* start migrating here */}
            {BetSizeFixedPercentageButton(10)}
            {BetSizeFixedPercentageButton(25)}
            {BetSizeFixedPercentageButton(50)}
            {BetSizeFixedPercentageButton(75)}
            {BetSizeMax()}
          </div>
        </div>

        {BetSizeSlider()}

        <div className='LowerSection' >
          <div className='BetSizesContainer' >
            <div className='BetSizeFields'>
              {BetSizeNumericField_crypto()}
              {BetSizeNumericField_fiat()}
            </div>
            {BetSizeSaverDiskette()}
          </div>
          {WalletBalance()}
          <div>
            {GasControls()}
          </div>
          <div className={'BetButtons' + (isMobile ? ' Mobile' : '')}>
            {CancelButton()}
            {BetButton()}
          </div>
        </div>
      </div>
    </CardBaseFC>
  );
}

export default CardNextBetting;
